import { useEffect, useState } from "react";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import QueueTable, { useQueue } from "@/components/QueueTable";
import { api, formatErr } from "@/lib/api";
import { fmtWhen } from "@/components/ReviewBits";
import { UserPlus, Pause, Play, Trash, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const err = (e) => toast.error(formatErr(e?.response?.data?.detail) || e.message);

export default function AdminReviewers() {
  const [ov, setOv] = useState(null);
  const [rows, reloadRows] = useQueue({});
  const load = () => api.get("/reviews/admin/overview").then((r) => setOv(r.data)).catch(err);
  const refresh = () => { load(); reloadRows(); };
  useEffect(() => { load(); }, []);

  const s = ov?.by_status || {};
  const active = (ov?.reviewers || []).filter((r) => r.active);
  return (
    <DashboardShell title="Credit reviewers" subtitle="Assignments, daily capacity, escalations and manual reassignment for every loan review."
      actions={<button onClick={() => api.post("/reviews/admin/run-cycle").then((r) => { toast.success(`Assigned ${r.data.assigned} · escalated ${r.data.escalated}`); refresh(); }).catch(err)} className="btn-outline !py-2 text-sm" data-testid="admin-run-cycle"><ArrowsClockwise size={14} /> Run assignment now</button>}>
      <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <StatCard label="Pending Assignment" value={ov ? s.pending_assignment : "…"} accent="warning" />
        <StatCard label="Pending Review" value={ov ? s.pending_review : "…"} accent="warning" />
        <StatCard label="In Review" value={ov ? s.in_review : "…"} />
        <StatCard label="Approved" value={ov ? s.approved : "…"} accent="success" />
        <StatCard label="Declined" value={ov ? s.declined : "…"} accent="warning" />
      </div>
      <ReviewerTable reviewers={ov?.reviewers || []} refresh={refresh} />
      <AddReviewer onAdded={refresh} />
      <div className="mt-8">
        <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2"><Warning size={18} weight="fill" className="text-[#C93B3B]" /> Escalated (not actioned in 48h)</h2>
        <QueueTable rows={ov ? ov.escalated : null} base="/admin/review" showReviewer />
      </div>
      <div className="mt-8">
        <h2 className="font-display text-xl font-medium mb-3">All applications</h2>
        <QueueTable rows={rows} base="/admin/review" showReviewer extra={(r) => <Reassign row={r} reviewers={active} onDone={refresh} />} />
      </div>
    </DashboardShell>
  );
}

function ReviewerTable({ reviewers, refresh }) {
  const toggle = (r) => api.patch(`/reviews/admin/reviewers/${r.id}`, { active: !r.active }).then(() => { toast.success(r.active ? "Reviewer paused" : "Reviewer resumed"); refresh(); }).catch(err);
  const remove = (r) => {
    if (!window.confirm(`Remove ${r.name}? Their open applications will be reassigned.`)) return;
    api.delete(`/reviews/admin/reviewers/${r.id}`).then((x) => { toast.success(`Removed · ${x.data.reassigned} reassigned, ${x.data.pending_assignment} queued`); refresh(); }).catch(err);
  };
  return (
    <div className="card overflow-x-auto" data-testid="reviewer-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[#4F5D4F] border-b border-[#E5E3DB]">
          <th className="py-2 pr-3">Reviewer</th><th className="py-2 pr-3">Open cases</th><th className="py-2 pr-3 w-56">Today's capacity</th><th className="py-2 pr-3">Last assigned</th><th className="py-2 pr-3">Status</th><th className="py-2" />
        </tr></thead>
        <tbody>
          {reviewers.map((r) => (
            <tr key={r.id} className="border-b border-[#F0EFEA]" data-testid={`reviewer-row-${r.id}`}>
              <td className="py-3 pr-3"><div className="font-medium">{r.name}</div><div className="text-xs text-[#4F5D4F]">{r.email}</div></td>
              <td className="py-3 pr-3 font-mono" data-testid={`reviewer-open-${r.id}`}>{r.open}</td>
              <td className="py-3 pr-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-[#E5E3DB] overflow-hidden"><div className={`h-full ${r.today >= r.cap ? "bg-[#C93B3B]" : "bg-[#0A3622]"}`} style={{ width: `${Math.min(100, (100 * r.today) / r.cap)}%` }} /></div>
                  <span className="font-mono text-xs" data-testid={`reviewer-capacity-${r.id}`}>{r.today}/{r.cap}</span>
                </div>
              </td>
              <td className="py-3 pr-3 text-xs text-[#4F5D4F]">{fmtWhen(r.last_assigned_at)}</td>
              <td className="py-3 pr-3"><span className={`badge ${r.active ? "badge-success" : "badge-neutral"}`}>{r.active ? "Active" : "Paused"}</span></td>
              <td className="py-3 text-right whitespace-nowrap">
                <button onClick={() => toggle(r)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`reviewer-toggle-${r.id}`}>{r.active ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}</button>
                <button onClick={() => remove(r)} className="btn-ghost !py-1.5 !px-3 text-xs text-[#C93B3B]" data-testid={`reviewer-remove-${r.id}`}><Trash size={12} /> Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddReviewer({ onAdded }) {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const ok = f.name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email) && f.password.length >= 8;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/reviews/admin/reviewers", f); toast.success("Credit reviewer added"); setF({ name: "", email: "", password: "" }); onAdded(); }
    catch (e2) { err(e2); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card mt-5 grid sm:grid-cols-4 gap-3 items-end" data-testid="add-reviewer-form">
      <label className="block"><span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Name</span><input className="input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-reviewer-name" /></label>
      <label className="block"><span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Email</span><input type="email" className="input mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="add-reviewer-email" /></label>
      <label className="block"><span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">Temporary password</span><input type="password" className="input mt-1" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="8+ characters" data-testid="add-reviewer-password" /></label>
      <button type="submit" disabled={!ok || busy} className="btn-primary justify-center disabled:opacity-50" data-testid="add-reviewer-submit"><UserPlus size={16} weight="fill" /> Add reviewer</button>
    </form>
  );
}

function Reassign({ row, reviewers, onDone }) {
  if (!["pending_review", "in_review", "pending_assignment"].includes(row.review_status)) return <span>{row.reviewer_name || "—"}</span>;
  const change = (id) => id && api.post(`/reviews/admin/${row.kind}/${row.id}/reassign`, { reviewer_id: id }).then(() => { toast.success("Application reassigned"); onDone(); }).catch(err);
  return (
    <select className="input !py-1.5 text-xs min-w-[160px]" value="" onChange={(e) => change(e.target.value)} data-testid={`reassign-select-${row.id}`}>
      <option value="">{row.reviewer_id ? row.reviewer_name : "Unassigned"}</option>
      {reviewers.filter((r) => r.id !== row.reviewer_id).map((r) => <option key={r.id} value={r.id}>{r.name} ({r.open} open)</option>)}
    </select>
  );
}
